import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { SlidersHorizontal } from "lucide-react";
import { useApp } from "../context/AppContext";
import { KIND_LABEL } from "../explore";
import Seo from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import ContentCard from "../components/ContentCard";
import Reveal from "../components/Reveal";
import { Skel } from "../components/Skeleton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const ATTR = {
  region: { madinah: { ar: "المدينة المنورة", en: "Al-Madinah" }, qassim: { ar: "القصيم", en: "Al-Qassim" }, alahsa: { ar: "الأحساء", en: "Al-Ahsa" }, riyadh: { ar: "الرياض وسدير", en: "Riyadh / Sudair" }, aljouf: { ar: "الجوف", en: "Al-Jouf" }, najran: { ar: "نجران", en: "Najran" }, alula: { ar: "العلا", en: "AlUla" }, bisha: { ar: "بيشة", en: "Bisha" } },
  sweetness: { low: { ar: "حلاوة خفيفة", en: "Mild" }, medium: { ar: "حلاوة معتدلة", en: "Medium" }, high: { ar: "حلاوة عالية", en: "Very sweet" } },
  texture: { soft: { ar: "طري", en: "Soft" }, "semi-dry": { ar: "نصف جاف", en: "Semi-dry" }, dry: { ar: "جاف", en: "Dry" }, crunchy: { ar: "مقرمش", en: "Crunchy" } },
};

export default function CollectionPage({ kind }) {
  const { lang, t } = useApp();
  const [items, setItems] = useState(null);
  const [filters, setFilters] = useState({ region: null, sweetness: null, texture: null });

  useEffect(() => {
    setItems(null);
    setFilters({ region: null, sweetness: null, texture: null });
    axios.get(`${API}/content/${kind}`).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [kind]);

  const isVar = kind === "varieties";
  const list = useMemo(() => (items || []).filter((it) =>
    !isVar || Object.entries(filters).every(([k, v]) => !v || it.attrs?.[k] === v)), [items, filters, isVar]);

  return (
    <main className="content-page collection-page" data-testid={`collection-${kind}`}>
      <Seo title={KIND_LABEL[kind][lang]} description={t(`collection.${kind}.sub`)} />
      <div className="collection-head">
        <Breadcrumbs items={[{ label: KIND_LABEL[kind][lang] }]} />
        <span className="section-tag">{t("nav.explore")}</span>
        <h1 className="section-title" data-testid="collection-title">{KIND_LABEL[kind][lang]}</h1>
        <p className="section-sub">{t(`collection.${kind}.sub`)}</p>
      </div>

      {isVar && (
        <Link to="/varieties/directory" className="glass-card dir-banner" data-testid="varieties-directory-link">
          <span className="section-tag">{t("dir.tag")}</span>
          <strong>{t("dir.bannerTitle")}</strong>
          <span className="dir-banner-sub">{t("dir.bannerSub")}</span>
        </Link>
      )}
      {isVar && (
        <div className="filter-bar glass-card" data-testid="variety-filters">
          <span className="filter-label"><SlidersHorizontal size={14} /> {t("filters.label")}</span>
          {Object.entries(ATTR).map(([group, opts]) => (
            <div key={group} className="filter-group" data-testid={`filter-group-${group}`}>
              <small>{t(`filters.${group}`)}</small>
              <div className="filter-chips">
                {Object.entries(opts).map(([val, label]) => (
                  <button
                    key={val}
                    className={`filter-chip ${filters[group] === val ? "on" : ""}`}
                    onClick={() => setFilters((f) => ({ ...f, [group]: f[group] === val ? null : val }))}
                    data-testid={`filter-${group}-${val}`}
                  >
                    {label[lang]}
                  </button>
                ))}
              </div>
            </div>
          ))}
          <span className="filter-count" data-testid="variety-count">{list.length} / {(items || []).length}</span>
        </div>
      )}

      {items === null && <div className="content-grid">{Array.from({ length: isVar ? 8 : 3 }).map((_, i) => <Skel key={i} h={300} r={18} />)}</div>}
      {items && list.length === 0 && <p className="admin-muted" style={{ textAlign: "center", padding: 30 }} data-testid="variety-empty">{t("filters.none")}</p>}
      <div className="content-grid">
        {list.map((it, i) => (
          <Reveal key={it.slug} delay={(i % 4) * 90}>
            <ContentCard item={it} kind={kind} attrs={isVar ? it.attrs : null} />
          </Reveal>
        ))}
      </div>
    </main>
  );
}
